/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import {
	Injectable,
	type WritableSignal,
	computed,
	effect,
	inject,
	signal,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Supabase } from "@app/@classes/supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { Subject, takeUntil } from "rxjs";
import { AuthStateService } from "../shared/auth-state.service";
import { FeaturesService } from "../shared/features.service";

// Constants
const PRESENCE_ROOM = "route_presence";

// Interfaces
interface RouteStatus {
	route: string;
	parameters?: Record<string, unknown>;
}

@Injectable({
	providedIn: "root",
})
export class RealtimeRoutePresenceService extends Supabase {
	// Dependencies
	private readonly authStateService: AuthStateService = inject(AuthStateService);
	private readonly featureService: FeaturesService = inject(FeaturesService);
	private readonly router: Router = inject(Router);
	private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

	// Internal State
	private readonly presenceDestroy$ = new Subject<void>();
	private channel?: RealtimeChannel;
	private readonly _routeStatus: WritableSignal<string> = signal("");
	private readonly _routeParameters: WritableSignal<Record<string, unknown>> =
		signal({});
	private readonly _usersRouteStatus: WritableSignal<
		Record<string, RouteStatus>
	> = signal({});

	// Computed Properties
	private readonly userId = computed(
		() => this.authStateService.currentUserId() || "",
	);
	private readonly tenantId = computed(() =>
		this.authStateService.currentTenantId(),
	);

	private readonly routesPresenceMap = computed(() => {
		const usersRouteStatus = this._usersRouteStatus();
		const routesMap: Record<string, string[]> = {};

		for (const userId in usersRouteStatus) {
			const status = usersRouteStatus[userId];
			if (!status || !status.route) continue;

			if (!routesMap[status.route]) {
				routesMap[status.route] = [];
			}
			routesMap[status.route].push(userId);
		}

		return routesMap;
	});

	// Public API
	public readonly usersOnSameRoute = computed<string[]>(() => {
		const currentRoute = this._routeStatus();
		const currentParams = this._routeParameters();
		const routesMap = this.routesPresenceMap();
		const usersStatus = this._usersRouteStatus();

		return (routesMap[currentRoute] || []).filter((userId) => {
			// Skip current user
			if (userId === this.userId()) return false;

			// Get user's status
			const userStatus = usersStatus[userId];
			if (!userStatus) return false;

			// If there are parameters to match, ensure both users have matching values
			const userParams = userStatus.parameters || {};
			if (
				Object.keys(currentParams).length !== Object.keys(userParams).length
			) {
				return false;
			}

			// Check if parameters match when present
			if (currentParams) {
				for (const key in currentParams) {
					if (userParams[key] !== currentParams[key]) {
						return false;
					}
				}
			}

			return true;
		});
	});

	constructor() {
		super();
		this.initializeSubscriptions();

		effect(() => {
			this.syncStatus();
		});
	}

	// Public methods
	public updateParameters(parameters: Record<string, unknown>): void {
		this._routeParameters.set(parameters);
	}

	public resetParameters(): void {
		this._routeParameters.set({});
	}

	// Lifecycle methods
	ngOnDestroy(): void {
		this.presenceDestroy$.next();
		this.presenceDestroy$.complete();
		this.destroyPresence();
	}

	// Private methods
	private initializeSubscriptions(): void {
		// Route change subscription
		this.router.events
			.pipe(takeUntil(this.presenceDestroy$))
			.subscribe((event) => {
				if (event instanceof NavigationEnd) {
					this._routeStatus.set(event.urlAfterRedirects);
					this._routeParameters.set({}); // Reset parameters on route change
				}
			});

		// Tenant change subscription
		toObservable(this.authStateService.currentTenantId)
			.pipe(takeUntil(this.presenceDestroy$))
			.subscribe((tenantId) => {
				if (!tenantId) {
					this.destroyPresence();
				} else if (!this.channel) {
					this.initPresence();
				}
			});

		// User change subscription
		toObservable(this.authStateService.currentUserId)
			.pipe(takeUntil(this.presenceDestroy$))
			.subscribe((userId) => {
				if (!userId) {
					this.destroyPresence();
				} else {
					this.initPresence();
				}
			});
	}

	private async syncStatus(): Promise<void> {
		if (!this.channel || !this.userId()) return;

		await this.channel.track({
			route: this._routeStatus(),
			parameters: this._routeParameters(),
		});
	}

	private initPresence(): void {
		if (this.channel) return;
		if (!this.tenantId()) return;
		if (!this.userId()) return;

		this.channel = this.createPresenceChannel();
		this.setupPresenceHandlers();
	}

	private createPresenceChannel(): RealtimeChannel {
		return this.supabaseClient.channel(`${PRESENCE_ROOM}_${this.tenantId()}`, {
			config: {
				presence: {
					key: this.userId(),
				},
			},
		});
	}

	private setupPresenceHandlers(): void {
		if (!this.channel) return;

		this.channel
			.on("presence", { event: "sync" }, () => this.handlePresenceSync())
			.on("presence", { event: "join" }, ({ key, newPresences }) => {
				// User has joined, handled by sync
			})
			.on("presence", { event: "leave" }, ({ key }) =>
				this.handlePresenceLeave(key),
			)
			.subscribe(async (status) => {
				if (status === "SUBSCRIBED") {
					this.syncStatus();
				}
			});
	}

	private handlePresenceSync(): void {
		const newState = this.channel?.presenceState<RouteStatus>();
		if (!newState) return;

		const usersIds = Object.keys(newState);
		const newRouteState: Record<string, RouteStatus> = {};

		for (const userId of usersIds) {
			for (const state of newState[userId]) {
				newRouteState[userId] = {
					route: state.route || "",
					parameters: state.parameters || {},
				};
			}
		}

		this._usersRouteStatus.set(newRouteState);
	}

	private handlePresenceLeave(key: string): void {
		this._usersRouteStatus.update((val) => {
			const newVal = { ...val };
			delete newVal[key];
			return newVal;
		});
	}

	private async destroyPresence(): Promise<void> {
		await this.channel?.untrack();
		await this.channel?.unsubscribe();
		this.channel = undefined;
		this._usersRouteStatus.set({});
		this._routeParameters.set({});
	}
}
